import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl, ValidationErrors } from '@angular/forms';
import { FloatLabelType } from '@angular/material/form-field';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  MunilyTextFieldAppearance,
  MunilyTextFieldErrorMessages,
  MunilyTextFieldSize,
  MunilyTextFieldWidthLayout,
} from './text-field.types';

@Component({
  selector: 'app-text-field',
  templateUrl: './text-field.component.html',
  styleUrls: ['./text-field.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TextFieldComponent implements OnInit, OnChanges, OnDestroy {
  @Input() control?: FormControl;

  @Input() appearance: MunilyTextFieldAppearance = 'outlined';
  @Input() size: MunilyTextFieldSize = 'default';
  /**
   * `ds` (por defecto): `width: 100%` con `max-width` del token Figma (`$munily-tf-field-max-width-ds`, 292px).
   * `fill`: sin tope de ancho; ocupa el 100% del padre (formularios en columnas / modales a ancho completo).
   */
  @Input() widthLayout: MunilyTextFieldWidthLayout = 'ds';
  /**
   * Solo aplica con `widthLayout === 'ds'`. Valor CSS para `max-width` (p. ej. `400px`, `min(100%, 320px)`).
   * Si se omite, se usa el token del DS.
   */
  @Input() maxWidth?: string;
  @Input() type: 'text' | 'password' | 'number' | 'email' | 'tel' = 'text';
  @Input() label = '';
  @Input() placeholder = '';
  @Input() hint = '';
  @Input() iconLeading?: string;
  @Input() iconTrailing?: string;
  @Input() prefixText?: string;
  @Input() suffixText?: string;
  @Input() hasCharacterCounter = false;
  @Input() maxLength?: number;
  /**
   * `auto` (por defecto): el label flota cuando hay valor o al enfocar (comportamiento Material).
   * `always` / `never`: ver documentación de Angular Material mat-form-field.
   */
  @Input() floatLabel: FloatLabelType = 'auto';
  @Input() disabled = false;
  @Input() successful = false;
  @Input() autocomplete = 'off';

  /** Mensaje de error fijo; tiene prioridad sobre errorMessages y los inferidos. */
  @Input() errorMessage = '';

  /**
   * Mapa validatorKey → mensaje para resolver errores del FormControl.
   * Ej: { required: 'Campo obligatorio', email: 'Email inválido' }
   * Si una key no está en el mapa, se usa un mensaje genérico por defecto.
   */
  @Input() errorMessages: MunilyTextFieldErrorMessages = {};

  @Output() valueChange = new EventEmitter<string>();

  @HostBinding('class.munily-tf-host--ds-default')
  get hostDsDefaultMaxWidth(): boolean {
    return this.widthLayout === 'ds' && !this.maxWidth;
  }

  @HostBinding('style.max-width')
  get hostMaxWidthStyle(): string | null {
    if (this.widthLayout === 'fill') {
      return 'none';
    }
    if (this.widthLayout === 'ds' && this.maxWidth) {
      return this.maxWidth;
    }
    return null;
  }

  readonly fallbackControl = new FormControl('');
  private readonly destroy$ = new Subject<void>();

  constructor(private readonly cdr: ChangeDetectorRef) {}

  get resolvedControl(): FormControl {
    return this.control ?? this.fallbackControl;
  }

  get materialAppearance(): 'outline' | 'fill' {
    return this.appearance === 'filled' ? 'fill' : 'outline';
  }

  get currentLength(): number {
    const v = this.resolvedControl.value;
    return v != null ? String(v).length : 0;
  }

  get resolvedErrorMessage(): string {
    if (this.errorMessage) return this.errorMessage;
    const errors = this.resolvedControl.errors;
    if (!errors) return '';
    return this.firstErrorMessage(errors);
  }

  ngOnInit(): void {
    this.syncDisabled();
    this.wireControlUpdates();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['disabled']) {
      this.syncDisabled();
    }
    if (changes['control']) {
      this.destroy$.next();
      this.syncDisabled();
      this.wireControlUpdates();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  showError(): boolean {
    const c = this.resolvedControl;
    return c.invalid && (c.touched || c.dirty);
  }

  showSuccessOutline(): boolean {
    return this.successful && !this.showError();
  }

  onInput(): void {
    this.valueChange.emit(this.resolvedControl.value as string);
  }

  onBlur(): void {
    this.resolvedControl.markAsTouched();
  }

  private syncDisabled(): void {
    const ctrl = this.resolvedControl;
    if (this.disabled && ctrl.enabled) {
      ctrl.disable({ emitEvent: false });
    } else if (!this.disabled && ctrl.disabled) {
      ctrl.enable({ emitEvent: false });
    }
  }

  private wireControlUpdates(): void {
    const ctrl = this.resolvedControl;
    ctrl.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.cdr.markForCheck());
    ctrl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.cdr.markForCheck());
  }

  private firstErrorMessage(errors: ValidationErrors): string {
    const key = Object.keys(errors)[0];
    const custom = this.errorMessages[key];
    if (custom) return custom;

    const err = errors[key];
    if (key === 'required') return 'Este campo es obligatorio';
    if (key === 'email') return 'Email no válido';
    if (key === 'minlength' && err?.requiredLength != null) return `Mínimo ${err.requiredLength} caracteres`;
    if (key === 'maxlength' && err?.requiredLength != null) return `Máximo ${err.requiredLength} caracteres`;
    if (key === 'min' || key === 'max') return 'Valor fuera de rango';
    if (typeof err === 'object' && err?.message) return String(err.message);
    return 'Valor no válido';
  }
}
